import AsyncSelect from "react-select/async";
import LicensePlate from "license-plate";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import licencePlateSelectBg from "../../../../general/fields/modules/commons/licencePlateSelectField/assets/licencePlateSelectBg.svg";
import { truckSchema } from "../../domain/schema/truckSchema";
import { CrudDataModel } from "../../../../../hooks/useCrud";
import useEntitySelect from "../../../../../hooks/useEntitySelect";
import useCrudFilters from "../../../../../hooks/useCrudFilters";
import { EntitySelectFieldBaseModel } from "../../../../general/fields/modules/commons/entitySelectField/entitySelectFieldModel";

export const TruckLicencePlateSelectField = ({
  isInvalid,
  onChange,
  value,
  isMulti = true,
  isClearable = true,
}: EntitySelectFieldBaseModel & {
  isMulti?: boolean;
  isClearable?: boolean;
}) => {
  const FILTER_KEY = "licence";
  const FILTER_TYPE = "contains";

  const { filters, updateFilter, getFilter, removeFilter } = useCrudFilters({
    searchBy: "SearchByRootTruck",
    fields: {},
  });

  const searchValue = getFilter(FILTER_KEY, FILTER_TYPE);

  const { t } = useTranslation("common");

  const { ref, options, loading, reload } = useEntitySelect({
    labelPrefix: FILTER_KEY,
    prefix: "trucks",
    entity: "Truck",
    schema: truckSchema,
    limit: 100,
    filters: filters,
    filterReloadOnSearch: true,
    value: value,
    optionsMap: (option) => {
      if (typeof option.label !== "string") return option;
      const plate = new LicensePlate(option.label);
      if (!plate.valid()) return option;
      return {
        value: option.value,
        label: plate.pretty(),
      };
    },
  });

  const [initialOptions, setInitialOptions] =
    useState<CrudDataModel[]>(options);

  const loadOptionsAsync = (inputValue: string) =>
    new Promise<CrudDataModel[]>((resolve) => {
      const sortOptions = (opts: CrudDataModel[]) =>
        opts.slice().sort((a, b) => {
          const labelA = typeof a.label === "string" ? a.label : "";
          const labelB = typeof b.label === "string" ? b.label : "";
          return labelA.localeCompare(labelB, undefined, {
            sensitivity: "base",
          });
        });
      if (!inputValue) {
        removeFilter(FILTER_KEY);
        void reload(filters).then((options) => {
          resolve(sortOptions(options));
        });
        return;
      }
      updateFilter(
        FILTER_KEY,
        inputValue.toUpperCase().replace(/-/g, ""),
        FILTER_TYPE,
        (filters) => {
          void reload(filters).then((options) => {
            resolve(sortOptions(options));
          });
        },
      );
    });

  useEffect(() => {
    void loadOptionsAsync("").then((opts) => setInitialOptions(opts));
  }, []);

  useEffect(() => {
    if (value === undefined) return ref.current?.setValue([], "select-option");
  }, [value]);

  useEffect(() => {
    if (value === undefined || !Array.isArray(value) || !onChange) return;
    const valueOptions = value.map((option) => {
      const plate = new LicensePlate(option.toString());
      return {
        label: plate.pretty(),
        value: option,
      };
    });
    onChange(valueOptions);
    ref.current?.setValue(valueOptions, "select-option");
  }, []);

  return (
    <div className={`entity-select-wrapper${isInvalid ? " is-invalid" : ""}`}>
      <AsyncSelect
        placeholder={t("Select...")}
        ref={ref}
        className={"entity-select"}
        classNamePrefix={"entity-select"}
        loadOptions={loadOptionsAsync}
        defaultOptions={initialOptions}
        cacheOptions
        value={
          value
            ? initialOptions.find((option) => option.value === value)
            : undefined
        }
        isLoading={loading}
        onChange={(options: unknown) => {
          removeFilter(FILTER_KEY);
          if (onChange) {
            onChange(options);
          }
        }}
        isClearable={isClearable}
        isMulti={isMulti}
        noOptionsMessage={() => {
          if (searchValue !== undefined)
            return t("No results found for '{{searchValue}}'", {
              searchValue,
            });
          return t("Search for a license plate");
        }}
        styles={{
          multiValue: (base) => ({
            ...base,
            background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
            width: "112px",
            height: "23px",
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "flex-end",
            alignItems: "center",
          }),
          singleValue: (base) => ({
            ...base,
            background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
            width: "112px",
            height: "23px",
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "flex-end",
            alignItems: "center",
            paddingLeft: "24px",
          }),
          multiValueRemove: () => ({
            color: "#fff",
            height: "23px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "15px",
          }),
          multiValueLabel: () => ({
            fontSize: "14px",
            fontWeight: 500,
            color: "#000",
            height: "auto",
            width: "100%",
            paddingLeft: "5px",
            letterSpacing: "2px",
          }),
        }}
      />
    </div>
  );
};
